import { useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiErrorDetail } from "../../lib/authApi";
import { ROLE_LABEL } from "./accountShared";

const STATUS_ACTIONS = {
  suspend: { status: "suspended", title: "Suspend account?", description: "The user is signed out everywhere and cannot sign in until re-activated. RFQs, orders, documents and audit history are preserved.", cta: "Suspend", destructive: true },
  disable: { status: "disabled", title: "Disable account?", description: "Sign-in is blocked and password resets are refused until re-activated. All historical records stay linked to this account.", cta: "Disable", destructive: true },
  archive: { status: "archived", title: "Archive account?", description: "Archiving is the non-destructive alternative to deletion: the account is hidden from active use but every RFQ, order, document and audit record remains intact.", cta: "Archive", destructive: true },
};

/** Shared action runner for the table menu and the detail sheet: confirmations, API calls, toasts, audit-backed backend. */
export const useAccountActions = (onChanged) => {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reset, setReset] = useState(null);

  const call = async (fn, success) => {
    setBusy(true);
    try {
      const data = await fn();
      if (success) toast.success(success);
      setPending(null);
      onChanged?.(data);
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const run = (kind, user, extra = {}) => {
    if (STATUS_ACTIONS[kind]) return setPending({ kind, user, withNote: true, ...STATUS_ACTIONS[kind] });
    if (kind === "activate") return call(() => adminApi.patch(`/admin/accounts/${user.id}/status`, { status: "active", note: "" }).then((r) => r.data), "Account activated.");
    if (kind === "unlock") return call(() => adminApi.post(`/admin/accounts/${user.id}/unlock`).then((r) => r.data), "Login lock cleared.");
    if (kind === "force-logout") return setPending({ kind, user, title: "Force logout?", description: "Every issued session token for this account is revoked immediately; the user must sign in again.", cta: "Force logout", destructive: true });
    if (kind === "reset-password") return setPending({ kind, user, title: user.has_password ? "Issue password reset link?" : "Issue invitation link?", description: "A one-time link valid for 24 hours is generated. Any previous link is invalidated. No password is shown or stored.", cta: "Generate link" });
    if (kind === "role") return setPending({ kind, user, role: extra.role, withNote: true, title: `Change role to ${ROLE_LABEL[extra.role]}?`, description: "Role changes are restricted, audited and sign the user out everywhere. Backend validation applies.", cta: "Change role", destructive: extra.role === "admin" });
    return null;
  };

  const confirm = async ({ note, confirm_email }) => {
    const { kind, user } = pending;
    if (STATUS_ACTIONS[kind]) return call(() => adminApi.patch(`/admin/accounts/${user.id}/status`, { status: STATUS_ACTIONS[kind].status, note }).then((r) => r.data), `Account ${STATUS_ACTIONS[kind].status}.`);
    if (kind === "force-logout") return call(() => adminApi.post(`/admin/accounts/${user.id}/force-logout`).then((r) => r.data), "All sessions revoked.");
    if (kind === "role") return call(() => adminApi.patch(`/admin/accounts/${user.id}/role`, { role: pending.role, confirm_email, note }).then((r) => r.data), "Role changed.");
    if (kind === "reset-password") {
      const data = await call(() => adminApi.post(`/admin/accounts/${user.id}/reset-password`).then((r) => r.data));
      if (data) setReset({ ...data, email: user.email });
    }
    return null;
  };

  return { pending, busy, reset, run, confirm, cancel: () => setPending(null), closeReset: () => setReset(null), showReset: setReset };
};
